import React from 'react';
import { useLocation } from 'react-router-dom';
import { ErrorBoundary, ErrorBoundaryProps } from './ErrorBoundary';

/**
 * RouteErrorBoundary Component
 *
 * A route-aware error boundary that automatically resets its error state
 * when the user navigates to a different route.
 *
 * This solves the problem where an error occurs on one page, and pressing
 * the browser back button still shows the error until page refresh.
 *
 * Implementation: Uses React's `key` prop to force a remount of the
 * ErrorBoundary when the location changes, which resets its internal state.
 *
 * @example
 * ```tsx
 * // In a layout component
 * <RouteErrorBoundary fallback={ErrorFallback}>
 *   <Outlet />
 * </RouteErrorBoundary>
 * ```
 */
export const RouteErrorBoundary: React.FC<ErrorBoundaryProps> = ({
  children,
  ...props
}): React.ReactElement => {
  const location = useLocation();

  // Use pathname + search as key to reset error boundary on navigation
  // This forces React to remount the ErrorBoundary when route changes
  const locationKey = `${location.pathname}${location.search}`;

  return (
    <ErrorBoundary key={locationKey} {...props}>
      {children}
    </ErrorBoundary>
  );
};
